'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { useInstantSearch, useSearchBox } from 'react-instantsearch';
import { useIsPopNavigation } from 'commons/navState';
import { NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import useAdvancedSearchContext from '../../hooks/useAdvancedSearchContext';
import useFilterTransitionState from '../../hooks/useFilterTransitionState';
import AdvancedSearchProductsGrid from '../AdvancedSearchProductsGrid';
import LoadMoreButton from '../AdvancedSearchProductsGrid/LoadMoreButton';
import InitialProductsGrid from './InitialProductsGrid';
import ProductsGridSkeleton from './ProductsGridSkeleton';
import useHitsAccumulation from './hooks/useHitsAccumulation';
import useDisplayProducts from './hooks/useDisplayProducts';
import useScrollPersistence from './hooks/useScrollPersistence';
const AdvancedSearchProductHits = t0 => {
  const $ = _c(82);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onProductsChange,
    initialProducts,
    seedServerData,
    eagerFirstPageImages: t2
  } = t1;
  const eagerFirstPageImages = t2 === undefined ? false : t2;
  const {
    items,
    isLastPage,
    showMore
  } = useHitsAccumulation();
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      skipSuspense: true
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    results,
    status,
    setIndexUiState,
    indexUiState
  } = useInstantSearch(t3);
  const {
    query: searchBoxQuery
  } = useSearchBox();
  const {
    isReadyToShipActive,
    minWidth,
    maxWidth,
    activeTypeFilter,
    pagesUrlParam,
    updateUrlParamImmediate,
    selectedFacets
  } = useAdvancedSearchContext();
  const isPopNavigation = useIsPopNavigation();
  let t4;
  if ($[3] !== isPopNavigation) {
    t4 = isPopNavigation();
    $[3] = isPopNavigation;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const isPopRef = useRef(t4);
  const scrollRestoredRef = useRef(false);
  const targetPagesRef = useRef(pagesUrlParam);
  let t5;
  let t6;
  if ($[5] !== activeTypeFilter || $[6] !== setIndexUiState) {
    t5 = () => {
      setIndexUiState(prev => {
        const currentType = prev?.refinementList?.type?.[0] ?? null;
        if (currentType === activeTypeFilter) {
          return prev;
        }
        const newRefinementList = {
          ...(prev.refinementList || {})
        };
        if (activeTypeFilter) {
          newRefinementList.type = [activeTypeFilter];
        } else {
          delete newRefinementList.type;
        }
        return {
          ...prev,
          refinementList: newRefinementList
        };
      });
    };
    t6 = [activeTypeFilter, setIndexUiState];
    $[5] = activeTypeFilter;
    $[6] = setIndexUiState;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  const currentPageCount = Math.ceil((items?.length || 0) / NUMBER_OF_PRODUCTS_TO_SHOW);
  let t7;
  if ($[9] !== currentPageCount || $[10] !== isLastPage || $[11] !== items?.length || $[12] !== showMore || $[13] !== status) {
    t7 = () => {
      if (status !== "idle" || !items?.length || isLastPage) {
        return;
      }
      if (currentPageCount - 1 < targetPagesRef.current) {
        showMore();
      }
    };
    $[9] = currentPageCount;
    $[10] = isLastPage;
    $[11] = items?.length;
    $[12] = showMore;
    $[13] = status;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = items?.length;
  let t9;
  if ($[15] !== currentPageCount || $[16] !== isLastPage || $[17] !== showMore || $[18] !== status || $[19] !== t8) {
    t9 = [currentPageCount, status, t8, isLastPage, showMore];
    $[15] = currentPageCount;
    $[16] = isLastPage;
    $[17] = showMore;
    $[18] = status;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  useEffect(t7, t9);
  indexUiState?.refinementList;
  let t10;
  if ($[21] !== indexUiState?.refinementList || $[22] !== isReadyToShipActive || $[23] !== maxWidth || $[24] !== minWidth || $[25] !== selectedFacets) {
    t10 = JSON.stringify({
      refinementList: indexUiState?.refinementList || {},
      isReadyToShipActive,
      minWidth,
      maxWidth,
      selectedFacets: selectedFacets ? Object.fromEntries(Object.entries(selectedFacets).map(_temp)) : undefined
    });
    $[21] = indexUiState?.refinementList;
    $[22] = isReadyToShipActive;
    $[23] = maxWidth;
    $[24] = minWidth;
    $[25] = selectedFacets;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const filterStateKey = t10;
  let t11;
  if ($[27] !== activeTypeFilter || $[28] !== filterStateKey || $[29] !== items || $[30] !== setIndexUiState || $[31] !== status || $[32] !== updateUrlParamImmediate) {
    t11 = {
      filterStateKey,
      setIndexUiState,
      items,
      status,
      activeTypeFilter,
      updateUrlParamImmediate,
      isPopRef,
      scrollRestoredRef,
      targetPagesRef
    };
    $[27] = activeTypeFilter;
    $[28] = filterStateKey;
    $[29] = items;
    $[30] = setIndexUiState;
    $[31] = status;
    $[32] = updateUrlParamImmediate;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const {
    isFilterTransitioning,
    hasSearchCompleted,
    showNoResults
  } = useFilterTransitionState(t11);
  let t12;
  if ($[34] !== seedServerData) {
    t12 = seedServerData?.products?.length ? {
      products: seedServerData.products,
      upholsteryOptions: seedServerData.upholsteryOptions
    } : undefined;
    $[34] = seedServerData;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const seed = t12;
  let t13;
  if ($[36] !== items || $[37] !== results || $[38] !== searchBoxQuery || $[39] !== seed) {
    t13 = {
      items,
      searchBoxQuery,
      results,
      seed
    };
    $[36] = items;
    $[37] = results;
    $[38] = searchBoxQuery;
    $[39] = seed;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const {
    displayProducts,
    loading,
    productIds,
    productIdsRaw
  } = useDisplayProducts(t13);
  const t14 = items?.length;
  let t15;
  if ($[41] !== currentPageCount || $[42] !== loading || $[43] !== productIds.length || $[44] !== productIdsRaw.length || $[45] !== status || $[46] !== t14) {
    t15 = {
      isPopRef,
      scrollRestoredRef,
      targetPagesRef,
      status,
      currentPageCount,
      itemsLength: t14,
      loading,
      productIdsLength: productIds.length,
      productIdsRawLength: productIdsRaw.length
    };
    $[41] = currentPageCount;
    $[42] = loading;
    $[43] = productIds.length;
    $[44] = productIdsRaw.length;
    $[45] = status;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  useScrollPersistence(t15);
  let t16;
  let t17;
  if ($[48] !== displayProducts || $[49] !== onProductsChange) {
    t16 = () => {
      if (onProductsChange && displayProducts?.length > 0) {
        onProductsChange(displayProducts);
      }
    };
    t17 = [displayProducts, onProductsChange];
    $[48] = displayProducts;
    $[49] = onProductsChange;
    $[50] = t16;
    $[51] = t17;
  } else {
    t16 = $[50];
    t17 = $[51];
  }
  useEffect(t16, t17);
  let t18;
  if ($[52] !== pagesUrlParam || $[53] !== showMore || $[54] !== updateUrlParamImmediate) {
    t18 = () => {
      const nextPages = pagesUrlParam + 1;
      showMore();
      targetPagesRef.current = nextPages;
      updateUrlParamImmediate?.({
        pages: nextPages
      });
    };
    $[52] = pagesUrlParam;
    $[53] = showMore;
    $[54] = updateUrlParamImmediate;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  const handleShowMore = t18;
  const isRefreshing = status === "loading" || status === "stalled" || loading;
  const showInitialProducts = initialProducts?.length > 0 && !hasSearchCompleted;
  if (!items?.length) {
    const showSkeleton = status === "loading" || status === "stalled" || isFilterTransitioning || !hasSearchCompleted || !showNoResults;
    if (showSkeleton) {
      if (showInitialProducts) {
        const t19 = !isLastPage;
        let t20;
        if ($[56] !== handleShowMore || $[57] !== initialProducts || $[58] !== loading || $[59] !== t19) {
          t20 = <InitialProductsGrid products={initialProducts} expectedCount={NUMBER_OF_PRODUCTS_TO_SHOW} showLoadMore={t19} onLoadMore={handleShowMore} isLoadingMore={loading} />;
          $[56] = handleShowMore;
          $[57] = initialProducts;
          $[58] = loading;
          $[59] = t19;
          $[60] = t20;
        } else {
          t20 = $[60];
        }
        return t20;
      }
      let t19;
      if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = <ProductsGridSkeleton count={NUMBER_OF_PRODUCTS_TO_SHOW} />;
        $[61] = t19;
      } else {
        t19 = $[61];
      }
      return t19;
    }
    let t19;
    if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <div role="status" aria-live="polite" className="py-8 text-center">No results found.</div>;
      $[62] = t19;
    } else {
      t19 = $[62];
    }
    return t19;
  }
  if (!displayProducts?.length) {
    if (showInitialProducts) {
      const t19 = !isLastPage;
      let t20;
      if ($[63] !== handleShowMore || $[64] !== initialProducts || $[65] !== items.length || $[66] !== loading || $[67] !== t19) {
        t20 = <InitialProductsGrid products={initialProducts} expectedCount={items.length} showLoadMore={t19} onLoadMore={handleShowMore} isLoadingMore={loading} />;
        $[63] = handleShowMore;
        $[64] = initialProducts;
        $[65] = items.length;
        $[66] = loading;
        $[67] = t19;
        $[68] = t20;
      } else {
        t20 = $[68];
      }
      return t20;
    }
    let t19;
    if ($[69] !== items.length) {
      t19 = <ProductsGridSkeleton count={items.length} />;
      $[69] = items.length;
      $[70] = t19;
    } else {
      t19 = $[70];
    }
    return t19;
  }
  const t19 = isRefreshing ? "animate-pulse" : "";
  const t20 = eagerFirstPageImages ? NUMBER_OF_PRODUCTS_TO_SHOW : 0;
  let t21;
  if ($[71] !== displayProducts || $[72] !== t20) {
    t21 = <AdvancedSearchProductsGrid products={displayProducts} eagerImageCount={t20} />;
    $[71] = displayProducts;
    $[72] = t20;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  let t22;
  if ($[74] !== handleShowMore || $[75] !== isLastPage || $[76] !== loading) {
    t22 = !isLastPage && <div className="flex justify-center mt-8 mb-12"><LoadMoreButton onClick={handleShowMore} isLoading={loading} /></div>;
    $[74] = handleShowMore;
    $[75] = isLastPage;
    $[76] = loading;
    $[77] = t22;
  } else {
    t22 = $[77];
  }
  let t23;
  if ($[78] !== t19 || $[79] !== t21 || $[80] !== t22) {
    t23 = <div className={t19}>{t21}{t22}</div>;
    $[78] = t19;
    $[79] = t21;
    $[80] = t22;
    $[81] = t23;
  } else {
    t23 = $[81];
  }
  return t23;
};
export default AdvancedSearchProductHits;
function _temp(t0) {
  const [attr, values] = t0;
  return [attr, [...values].sort()];
}